import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, UserPlus } from 'lucide-react';

// High-quality background images
const BACKGROUND_IMAGES = [
  '/images/back1.png',
  '/images/back2.png', 
  '/images/back3.png',
  '/images/back4.png'
];



export default function Home() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Preload all background images
  useEffect(() => {
    const preloadImages = async () => {
      const imagePromises = BACKGROUND_IMAGES.map((src) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = reject;
          img.src = src;
        });
      });

      try {
        await Promise.all(imagePromises);
        setImagesLoaded(true);
      } catch (error) {
        console.error('Error preloading images:', error);
        setImagesLoaded(true); // Still set to true to show content
      }
    };

    preloadImages();
  }, []);

  // Auto-slide background images
  useEffect(() => {
    if (!imagesLoaded) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % BACKGROUND_IMAGES.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [imagesLoaded]);

  if (!imagesLoaded) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-white/80 animate-pulse flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-white/80 border-t-transparent rounded-full animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black text-white overflow-hidden">
      {/* Premium Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50">
        <div className="bg-white/5 backdrop-blur-2xl border-b border-white/10 shadow-2xl">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <img src="/logo-bvg-white.png" alt="BVG" className="w-8 h-8" />
                <span className="text-xl font-['Plus_Jakarta_Sans'] font-medium tracking-wide">BIG VISION GAMES</span>
              </div>
              
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => navigate('/login')}
                  className="px-6 py-2 bg-white/10 backdrop-blur-xl border border-white/20 text-white rounded-full font-['Plus_Jakarta_Sans'] font-medium hover:bg-white/20 transition-all duration-200"
                >
                  Sign In
                </button>
                <button 
                  onClick={() => navigate('/login?register=true')}
                  className="px-6 py-2 bg-white text-black rounded-full font-['Plus_Jakarta_Sans'] font-medium hover:bg-white/90 transition-all duration-200"
                >
                  Register
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with High-Quality Backgrounds */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Optimized Dynamic Background */}
        <div className="absolute inset-0">
          {BACKGROUND_IMAGES.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                currentSlide === index ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image}
                alt=""
                className="w-full h-full object-cover"
                loading="eager"
                decoding="async"
                style={{
                  willChange: 'opacity',
                  transform: 'translateZ(0)', // Force GPU acceleration
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/50" />
            </div>
          ))}
        </div>

        {/* Reduced Floating Orbs for performance */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-3/4 right-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="space-y-8">
            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="text-6xl md:text-8xl lg:text-9xl font-thin tracking-tight">
                <span className="block opacity-0 animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
                  Gaming
                </span>
                <span className="block bg-gradient-to-r from-purple-400 via-pink-500 to-indigo-500 bg-clip-text text-transparent opacity-0 animate-fade-in-up" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
                  Made Simple
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl font-light text-white/80 max-w-3xl mx-auto opacity-0 animate-fade-in-up font-['Plus_Jakarta_Sans']" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}>
                Your premier destination for the latest video games, consoles, and gaming peripherals. 
                Competitive prices, fast delivery, and exceptional customer service.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}>
              <button 
                onClick={() => navigate('/login')}
                className="group px-8 py-4 bg-white text-black rounded-full font-['Plus_Jakarta_Sans'] font-medium transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-white/25 flex items-center gap-3"
              >
                <span>Sign In</span>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>
              
              <button 
                onClick={() => navigate('/login?register=true')}
                className="group px-8 py-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full font-['Plus_Jakarta_Sans'] font-medium transition-all duration-300 hover:bg-white/20 flex items-center gap-3"
              >
                <UserPlus className="w-5 h-5" />
                <span>Register</span>
              </button>
            </div>

            {/* Stats Section */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 opacity-0 animate-fade-in-up" style={{ animationDelay: '1.0s', animationFillMode: 'forwards' }}>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white mb-1 font-['Plus_Jakarta_Sans']">Free 24h</div>
                <div className="text-white/70 text-sm md:text-base font-['Plus_Jakarta_Sans']">UK Delivery</div>
              </div>
              
              <div className="hidden md:block w-px h-12 bg-white/20"></div>
              
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white mb-1 font-['Plus_Jakarta_Sans']">4.9★</div>
                <div className="text-white/70 text-sm md:text-base font-['Plus_Jakarta_Sans']">Customer Rating</div>
              </div>
              
              <div className="hidden md:block w-px h-12 bg-white/20"></div>
              
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white mb-1 font-['Plus_Jakarta_Sans']">Price Match</div>
                <div className="text-white/70 text-sm md:text-base font-['Plus_Jakarta_Sans']">Guarantee</div>
              </div>
            </div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-20 right-8 z-20 flex flex-col gap-3">
          {BACKGROUND_IMAGES.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-1 h-8 rounded-full transition-all duration-300 ${
                currentSlide === index ? 'bg-white' : 'bg-white/30'
              }`}
            />
          ))}
        </div>
      </section>


    </div>
  );
} 
